import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Stepper } from "@/components/Stepper";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

import { CustomModelsTable } from "./CustomModelsTable";
import { useCustomModelsControls } from "./hooks/useCustomModelsControls";
import { CustomModelFormValues } from "./types";
import { useCustomModelsForm } from "./hooks/useCustomModelsForm";
import { useCustomParametersStore } from "./CustomModelsCreation/CustomParameters/custom-parameters-store";
import { ConfirmExitDialog } from "./ConfirmExitDialog";
import { getCustomModelCreationSteps } from "./CustomModelsCreation";
import FineTuneModal from "../FineTuneModal/FineTuneModal";
import { BrainCog } from "lucide-react";
import { useProviders } from "@/queries/providers/useProviders";
import { useAuth } from "@/context/auth-context";
import { Provider } from "@/queries/models";
import { useModelsStore } from "@/providers/ProviderAndModelsProvider/models-store";

export const CustomModelsCreation = observer(() => {
  const store = useCustomParametersStore();
  const { store: modelsStore } = useModelsStore({});
  const { setSelectedProviderId } = modelsStore;
  const authContext = useAuth();
  const methods = useForm<CustomModelFormValues>({
    mode: "onChange",
    defaultValues: {
      create_custom_model: {
        provider: "",
        modelName: "",
        displayName: "",
        modelType: "chat",
        apiKey: "",
      },
    },
  });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const {
    onEditModel,
    onOpenChange,
    isOpen,
    setIsOpen,
    activeStep,
    setActiveStep,
  } = useCustomModelsControls({
    setIsConfirmationModalOpen,
    methods,
  });

  const { data: providers, isLoading: isProvidersLoading } = useProviders({
    userToken: authContext?.userToken!,
  });

  const { isSubmitted, setIsSubmitted, onSubmit, isLoading, reset, values } =
    useCustomModelsForm({
      setIsOpen,
      methods,
      setActiveStep,
    });

  const steps = useMemo(
    () =>
      getCustomModelCreationSteps({
        models: store.modelsList || [],
        providers: providers || [],
        isEdit: store.isEditing,
        isViewing: store.isViewing,
        isLoading,
      }),
    [store.modelsList, store.isEditing, store.isViewing, isLoading, providers],
  );

  useEffect(() => {
    if (values.provider) {
      const provider = providers.find(
        (provider: Provider) => provider.name === values.provider,
      );

      if (provider) {
        setSelectedProviderId(provider.id);
      }
    }
  }, [providers, setSelectedProviderId, values.provider]);

  return (
    <>
      <CustomModelsTable onEditModel={onEditModel} />
      <ConfirmExitDialog
        reset={reset}
        isSubmitted={isSubmitted}
        setIsSubmitted={setIsSubmitted}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        isConfirmationModalOpen={isConfirmationModalOpen}
      />
      <div className="flex justify-between">
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
          <DialogTrigger disabled={isProvidersLoading} asChild>
            <Button type="button" variant="outline">
              Create Custom Model
            </Button>
          </DialogTrigger>
          <DialogContent className={activeStep > 0 ? "max-w-screen-md" : ""}>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Stepper
                  size="dialog"
                  activeStep={activeStep}
                  steps={steps}
                  isPreview={store.isViewing}
                  onNext={() => {
                    setActiveStep(activeStep + 1);
                  }}
                  onBack={() => {
                    setActiveStep(activeStep - 1);
                  }}
                  onExit={() => {
                    onOpenChange(false);
                  }}
                />
              </form>
            </FormProvider>
          </DialogContent>
        </Dialog>
        <FineTuneModal>
          <Button type="button" variant="ghost">
            <BrainCog className="mr-2 h-4 w-4" />
            Fine-Tune a Model
          </Button>
        </FineTuneModal>
      </div>
    </>
  );
});
